import { updateProfile, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";
import React, { useEffect, useState } from "react";
import {
  Button,
  Input,
  Flex,
  Stack,
  PasswordInput,
  Tabs,
  Text,
  Group,
  Badge,
  Loader,
  Center,
  SegmentedControl,
  Select,
  Switch,
} from "@mantine/core";
import { openConfirmModal } from "@mantine/modals";
import {
  IconAlertCircle,
  IconLock,
  IconPlugConnected,
  IconDeviceDesktop,
  IconSun,
  IconMoonStars,
  IconArmchair,
} from "@tabler/icons";
import { ShowNotificationError, ShowNotificationSuccess } from "../../components/Notifications";
import * as apiService from "../../api-service";
import { PersonAvatar } from "../../components/PersonAvatar";
import { useColorSchemeMode } from "../../components/ColorSchemeMode";
import { getSignInMethod, SIGN_IN_METHOD } from "../../components/AuthMethod";
import {
  getFavouriteDeskRoomId,
  setFavouriteDeskRoomId,
  getHarvestAutofillEnabled,
  setHarvestAutofillEnabled,
} from "../../services/UserPreferences";
import { getExtraRoles, EXTRA_ROLE_COLORS } from "../../services/UserRoles";

// A small icon+text combo reused for each Appearance option below - Mantine's SegmentedControl
// accepts any ReactNode as a data item's label, not just plain text.
function ModeOptionLabel({ icon: Icon, label }) {
  return (
    <Center sx={{ gap: 6 }}>
      <Icon size={15} stroke={1.5} />
      <Text size="sm">{label}</Text>
    </Center>
  );
}

// Matches the colour convention already used for role badges on the Employees page.
const ROLE_COLORS = {
  admin: "violet",
  employee: "blue",
  super: "yellow",
};

const MIN_PASSWORD_LENGTH = 8;
const PASSWORD_REQUIREMENTS_HINT = `At least ${MIN_PASSWORD_LENGTH} characters, with 1 uppercase letter, 1 lowercase letter, and 1 special character`;

// Enforced client-side before ever hitting Firebase - Firebase's own default policy only requires
// 6 characters and nothing else, which is weaker than what's asked for here, so this has to be our
// own check rather than something Firebase's weak-password error would catch for us.
function isPasswordValid(password) {
  return (
    password.length >= MIN_PASSWORD_LENGTH &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[^A-Za-z0-9]/.test(password)
  );
}

// Same "match on the raw Firebase message" approach as Login.js's own describeAuthError - kept as
// a separate copy here rather than shared, since the set of codes that actually matter differs
// (reauth/update-password vs sign-in): a mistyped Current Password comes back as wrong-password or
// invalid-credential depending on SDK version, and weak-password/requires-recent-login are unique
// to this flow. Without this, users saw the raw "Firebase: Error (auth/wrong-password)." string.
function describePasswordUpdateError(error) {
  const message = error?.message ?? "";
  if (message.includes("wrong-password") || message.includes("invalid-credential")) {
    return "Current password is incorrect.";
  }
  if (message.includes("too-many-requests")) {
    return "Too many attempts. Please wait a while before trying again.";
  }
  if (message.includes("weak-password")) {
    return `Password does not meet requirements: ${PASSWORD_REQUIREMENTS_HINT}.`;
  }
  if (message.includes("requires-recent-login")) {
    return "For your security, please sign out and back in, then try again.";
  }
  return message || "Something went wrong. Please try again.";
}

function Profile({ currentUser, userClaims, userToken, companyId, companyData, isHarvestForecastEnabled }) {
  const [newName, setNewName] = useState(currentUser?.displayName ?? "");
  const [isSavingName, setIsSavingName] = useState(false);

  const { mode, setMode } = useColorSchemeMode();

  // Desk rooms only (excludes meeting rooms), same filter/sort Booker itself uses for its own
  // "Select Room" dropdown (src/pages/booker/booker.js) - alphabetical rather than whatever order
  // Edit Company > Booker happens to list them in.
  const deskRooms = [...(companyData?.["rooms"] ?? [])]
    .filter((r) => !r.is_meeting)
    .sort((a, b) => a.title.localeCompare(b.title));

  const [favouriteRoomId, setFavouriteRoomId] = useState(null);
  const [isLoadingFavouriteRoom, setIsLoadingFavouriteRoom] = useState(true);
  const [isSavingFavouriteRoom, setIsSavingFavouriteRoom] = useState(false);

  useEffect(() => {
    if (!companyId || !currentUser?.uid) {
      setIsLoadingFavouriteRoom(false);
      return;
    }
    getFavouriteDeskRoomId(companyId, currentUser.uid)
      .then((roomId) => setFavouriteRoomId(roomId))
      .catch((err) => console.error(err))
      .finally(() => setIsLoadingFavouriteRoom(false));
  }, [companyId, currentUser?.uid]);

  // Auto-saves on change (same pattern as Appearance just below) rather than needing its own
  // Save button - there's nothing to double-check before committing a room preference, unlike
  // Display Name which is validated/trimmed first.
  const updateFavouriteRoom = (roomId) => {
    setFavouriteRoomId(roomId);
    setIsSavingFavouriteRoom(true);
    setFavouriteDeskRoomId(companyId, currentUser.uid, roomId)
      .then(() => ShowNotificationSuccess("Favourite Location Updated"))
      .catch((err) => ShowNotificationError("Error", err?.message ?? String(err)))
      .finally(() => setIsSavingFavouriteRoom(false));
  };

  // Harcast's "Add Time" pre-fill from the previous day's entry - on by default, auto-saves on
  // change like the favourite desk location above. Only offered once Harvest is enabled.
  const isHarvestEnabled = companyData?.["harvest_forecast"]?.["harvest_enabled"] ?? false;
  const [harvestAutofill, setHarvestAutofill] = useState(true);
  const [isLoadingHarvestAutofill, setIsLoadingHarvestAutofill] = useState(true);

  useEffect(() => {
    if (!companyId || !currentUser?.uid) {
      setIsLoadingHarvestAutofill(false);
      return;
    }
    getHarvestAutofillEnabled(companyId, currentUser.uid)
      .then((enabled) => setHarvestAutofill(enabled))
      .catch((err) => console.error(err))
      .finally(() => setIsLoadingHarvestAutofill(false));
  }, [companyId, currentUser?.uid]);

  const updateHarvestAutofill = (enabled) => {
    setHarvestAutofill(enabled);
    setHarvestAutofillEnabled(companyId, currentUser.uid, enabled)
      .then(() => ShowNotificationSuccess(enabled ? "Harcast Autofill Enabled" : "Harcast Autofill Disabled"))
      .catch((err) => {
        setHarvestAutofill(!enabled);
        ShowNotificationError("Error", err?.message ?? String(err));
      });
  };

  // Firebase has no password to reauthenticate with for a session that signed in via the
  // passwordless email link, so "Change Password" is meaningless (and would only fail with a
  // confusing reauth error) whenever that's how this particular session was established - see
  // components/AuthMethod.js for why this has to be tracked ourselves rather than asked of Firebase.
  const isPasswordlessSession = getSignInMethod() === SIGN_IN_METHOD.EMAIL_LINK;

  // "Connected Accounts" - only meaningful once Harvest & Forecast's per-user OAuth sign-in is
  // actually turned on for this company (Edit Company > Harvest & Forecast); otherwise there's
  // nothing here to ever be connected, so the tab is skipped entirely.
  const [connectionStatus, setConnectionStatus] = useState("checking"); // checking | connected | disconnected
  const [isRevoking, setIsRevoking] = useState(false);

  useEffect(() => {
    if (isHarvestForecastEnabled && userToken && companyId) {
      apiService
        .getHarvestOAuthStatus({ userIdToken: userToken, companyId })
        .then((data) => setConnectionStatus(data?.connected ? "connected" : "disconnected"))
        .catch((e) => {
          console.error(e);
          setConnectionStatus("disconnected");
        });
    }
  }, [isHarvestForecastEnabled, userToken, companyId]);

  const revokeHarvestAccess = () => {
    openConfirmModal({
      title: <Text weight={700}>Revoke Harvest Access?</Text>,
      centered: true,
      children: (
        <Text size="sm">
          You&apos;ll need to reconnect from the Harcast page to see your timesheet or schedule
          again.
        </Text>
      ),
      labels: { confirm: "Revoke Access", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => {
        setIsRevoking(true);
        apiService
          .deleteHarvestOAuthConnection({ userIdToken: userToken, companyId })
          .then(() => {
            setConnectionStatus("disconnected");
            setIsRevoking(false);
            ShowNotificationSuccess("Harvest Access Revoked");
          })
          .catch((e) => {
            console.error(e);
            setIsRevoking(false);
            ShowNotificationError("Failed to revoke access. Please try again.");
          });
      },
    });
  };

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordRepeat, setNewPasswordRepeat] = useState("");
  const [isPasswordVisible, setPasswordToggle] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  const setDisplayName = (name) => {
    setIsSavingName(true);
    updateProfile(currentUser, { displayName: name })
      .then(() => {
        setIsSavingName(false);
        ShowNotificationSuccess("Display Name Updated");
      })
      .catch((error) => {
        setIsSavingName(false);
        ShowNotificationError("Error", error?.message ?? String(error));
      });
  };

  return (
    <Stack spacing="lg">
      <Group noWrap align="flex-start">
        <PersonAvatar name={currentUser?.displayName} size={56} />
        <div style={{ minWidth: 0 }}>
          <Text fw={700} size="lg" lineClamp={1}>
            {currentUser?.displayName}
          </Text>
          <Text c="dimmed" size="sm" lineClamp={1}>
            {currentUser?.email}
          </Text>
          <Group spacing={6} mt={6}>
            {userClaims?.["access"] && (
              <Badge color={ROLE_COLORS[userClaims["access"]]} variant="light" sx={{ textTransform: "capitalize" }}>
                {userClaims["access"]}
              </Badge>
            )}
            {getExtraRoles(userClaims).map((role) => (
              <Badge key={role} color={EXTRA_ROLE_COLORS[role]} variant="light" sx={{ textTransform: "capitalize" }}>
                {role}
              </Badge>
            ))}
          </Group>
        </div>
      </Group>

      <Tabs defaultValue="details" variant="outline" radius="md">
        <Tabs.List>
          <Tabs.Tab value="details" icon={<IconAlertCircle size={14} />} sx={{ flexShrink: 0 }}>
            Details
          </Tabs.Tab>
          {!isPasswordlessSession && (
            <Tabs.Tab value="security" icon={<IconLock size={14} />} sx={{ flexShrink: 0 }}>
              Security
            </Tabs.Tab>
          )}
          {isHarvestForecastEnabled && (
            <Tabs.Tab value="connected-accounts" icon={<IconPlugConnected size={14} />} sx={{ flexShrink: 0 }}>
              Connected Accounts
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="details">
          <Stack p="lg" spacing="lg">
            <Input.Wrapper label="Display Name" required>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} autoComplete="given-name" radius="lg" mt={8} />
            </Input.Wrapper>
            <Flex justify="center">
              <Button
                loading={isSavingName}
                disabled={!newName.trim() || newName === currentUser?.displayName}
                onClick={() => setDisplayName(newName.trim())}
              >
                Update Details
              </Button>
            </Flex>

            <Input.Wrapper
              label="Theme"
              description="Device automatically follows your system's own light/dark setting - including if it changes later. Choosing Light or Dark overrides that and sticks regardless of what your device does."
            >
              <SegmentedControl
                mt={8}
                fullWidth
                value={mode}
                onChange={setMode}
                data={[
                  { value: "device", label: <ModeOptionLabel icon={IconDeviceDesktop} label="Device" /> },
                  { value: "light", label: <ModeOptionLabel icon={IconSun} label="Light" /> },
                  { value: "dark", label: <ModeOptionLabel icon={IconMoonStars} label="Dark" /> },
                ]}
              />
            </Input.Wrapper>

            {deskRooms.length > 0 && (
              <Select
                label="Favourite Desk Location"
                icon={<IconArmchair size={16} />}
                description="Automatically selected for you whenever you book a desk on Booker."
                placeholder="None"
                clearable
                searchable
                autoComplete="no"
                disabled={isLoadingFavouriteRoom || isSavingFavouriteRoom}
                value={favouriteRoomId}
                data={deskRooms.map((r) => ({ value: r.id, label: r.title }))}
                onChange={updateFavouriteRoom}
              />
            )}

            {isHarvestEnabled && (
              <Switch
                label="Autofill Harcast Time"
                description="When you add time in Harcast, pre-fill the project, task, hours and notes from your previous day's entry."
                checked={harvestAutofill}
                disabled={isLoadingHarvestAutofill}
                onChange={(e) => updateHarvestAutofill(e.currentTarget.checked)}
              />
            )}
          </Stack>
        </Tabs.Panel>

        {!isPasswordlessSession && (
          <Tabs.Panel value="security">
            <Stack p="lg" spacing="lg">
              <Input.Wrapper label="Current Password" required>
                <PasswordInput
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  radius="lg"
                  mt={8}
                />
              </Input.Wrapper>
              <Input.Wrapper
                label="New Password"
                description={PASSWORD_REQUIREMENTS_HINT}
                required
                error={newPassword.length > 0 && !isPasswordValid(newPassword) ? "Doesn't meet the requirements above" : null}
              >
                <PasswordInput
                  value={newPassword}
                  visible={isPasswordVisible}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onVisibilityChange={setPasswordToggle}
                  autoComplete="new-password"
                  radius="lg"
                  mt={8}
                />
              </Input.Wrapper>
              <Input.Wrapper
                label="Confirm New Password"
                required
                error={newPasswordRepeat.length > 0 && newPassword !== newPasswordRepeat ? "Passwords do not match" : null}
              >
                <PasswordInput
                  value={newPasswordRepeat}
                  visible={isPasswordVisible}
                  onChange={(e) => setNewPasswordRepeat(e.target.value)}
                  onVisibilityChange={setPasswordToggle}
                  autoComplete="new-password"
                  radius="lg"
                  mt={8}
                />
              </Input.Wrapper>
              <Flex justify="center">
                <Button
                  loading={isSavingPassword}
                  disabled={!currentPassword.length || !isPasswordValid(newPassword) || newPassword !== newPasswordRepeat}
                  onClick={() => {
                    setIsSavingPassword(true);
                    const cred = EmailAuthProvider.credential(currentUser.email, currentPassword);
                    reauthenticateWithCredential(currentUser, cred)
                      .then((credential) =>
                        updatePassword(credential.user, newPassword)
                          .then(() => {
                            setNewPassword("");
                            setCurrentPassword("");
                            setNewPasswordRepeat("");
                            setIsSavingPassword(false);
                            ShowNotificationSuccess("Password Updated");
                          })
                          .catch((error) => {
                            setIsSavingPassword(false);
                            ShowNotificationError("Error", describePasswordUpdateError(error));
                          })
                      )
                      .catch((error) => {
                        setIsSavingPassword(false);
                        ShowNotificationError("Error", describePasswordUpdateError(error));
                      });
                  }}
                >
                  Update Password
                </Button>
              </Flex>
            </Stack>
          </Tabs.Panel>
        )}

        {isHarvestForecastEnabled && (
          <Tabs.Panel value="connected-accounts">
            <Stack p="lg">
              {connectionStatus === "checking" ? (
                <Center py="xl">
                  <Loader size="sm" />
                </Center>
              ) : (
                <Group position="apart" align="center">
                  <div>
                    <Text weight={600}>Harcast</Text>
                    <Text size="xs" c="dimmed">
                      Used to show your timesheet and schedule.
                    </Text>
                  </div>
                  {connectionStatus === "connected" ? (
                    <Group spacing="lg" align="center">
                      <Badge color="teal" variant="light">
                        Connected
                      </Badge>
                      <Button color="red" variant="light" loading={isRevoking} onClick={revokeHarvestAccess}>
                        Revoke Access
                      </Button>
                    </Group>
                  ) : (
                    <Badge color="gray" variant="light">
                      Not Connected
                    </Badge>
                  )}
                </Group>
              )}
            </Stack>
          </Tabs.Panel>
        )}
      </Tabs>
    </Stack>
  );
}

export default Profile;
